import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  User,
  Shield,
  Key,
  Bell,
  Lock,
  Eye,
  EyeOff,
  Smartphone,
  QrCode,
  Copy,
  Check,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Download,
  Calendar,
  Activity,
  Globe,
  Settings as SettingsIcon,
  Save,
  X,
  Briefcase
} from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import { tokenStorage } from '../utils/tokenStorage';
import SystemSecuritySettings from '../components/SystemSecuritySettings';
import ExternalIntegrationsGate from '../components/Integrations/ExternalIntegrationsGate';
import SlackNotificationSettings from '../components/Integrations/SlackNotificationSettings';
import BDAssistantSettings from '../components/BD/BDAssistantSettings';
import BDAssistantRolloutCard from '../components/BD/BDAssistantRolloutCard';
import BDFollowUpFirmRulesCard from '../components/BD/BDFollowUpFirmRulesCard';
import { useExternalIntegrations } from '../hooks/useExternalIntegrations';

interface MFASetup {
  qr_code: string;
  secret: string;
  backup_codes: string[];
}

interface MfaFactor {
  id: number;
  type: 'totp' | 'email' | 'passkey' | 'sms' | 'voice';
  label: string | null;
  isDefault: boolean;
  verified: boolean;
  lastUsedAt: string | null;
}

const MFA_METHOD_LABELS: Record<string, string> = {
  totp: 'Authenticator app',
  email: 'Email',
  passkey: 'Passkey',
  sms: 'Text message',
  voice: 'Phone call',
};

interface TwilioConfig {
  enabled: boolean;
  accountSid: string | null;
  hasAuthToken: boolean;
  fromNumber: string | null;
  configured: boolean;
}

interface UserActivity {
  id: number;
  action: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'profile');
  // Whether this tenant may use external integrations at all. Decides if the
  // Notifications tab exists; the panel itself is gated again below so a
  // deep-linked ?tab=notifications cannot walk around it.
  const { allowed: integrationsAllowed } = useExternalIntegrations();
  const [loading, setLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<MfaFactor[]>([]);
  const [emailEnrolSent, setEmailEnrolSent] = useState(false);
  const [emailEnrolCode, setEmailEnrolCode] = useState('');
  const [twilioConfig, setTwilioConfig] = useState<TwilioConfig | null>(null);
  const [twilioForm, setTwilioForm] = useState({ accountSid: '', authToken: '', fromNumber: '' });
  const [twilioTesting, setTwilioTesting] = useState(false);
  const [twilioTestResult, setTwilioTestResult] = useState<string | null>(null);
  const [phoneEnrolStep, setPhoneEnrolStep] = useState<'idle' | 'code'>('idle');
  const [phoneNumberInput, setPhoneNumberInput] = useState('');
  const [phoneEnrolCode, setPhoneEnrolCode] = useState('');
  const [phoneEnrolMethod, setPhoneEnrolMethod] = useState<'sms' | 'voice'>('sms');
  const [mfaSetup, setMfaSetup] = useState<MFASetup | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [recentActivity, setRecentActivity] = useState<UserActivity[]>([]);
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);

  // Password validation function (same as backend)
  const validatePassword = (password: string) => {
    const errors: string[] = [];

    if (!password) {
      return { isValid: false, errors: ['Password is required'] };
    }

    if (password.length < 12) {
      errors.push('At least 12 characters long');
    }

    if (!/\d/.test(password)) {
      errors.push('At least one number');
    }

    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('At least one special character');
    }

    return { isValid: errors.length === 0, errors };
  };

  const handlePasswordChange = (password: string) => {
    setProfileData(prev => ({ ...prev, newPassword: password }));
    const validation = validatePassword(password);
    setPasswordErrors(validation.errors);
  };

  const [passwordMfaCode, setPasswordMfaCode] = useState('');
  const [showPasswordMfaInput, setShowPasswordMfaInput] = useState(false);
  const [ssoUser, setSsoUser] = useState(false);
  const [ssoProvider, setSsoProvider] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Ensure API has the latest token
    const token = tokenStorage.getAccessToken();
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    fetchUserSettings();
    fetchRecentActivity();
  }, []);

  const fetchUserSettings = async () => {
    try {
      const userResponse = await api.get('/api/auth/me');
      setProfileData(prev => ({
        ...prev,
        name: userResponse.data.user?.name || userResponse.data.name || '',
        email: userResponse.data.user?.email || userResponse.data.email || ''
      }));

      const userData = userResponse.data.user || userResponse.data;
      if (userData.sso_provider && userData.sso_provider !== 'local') {
        setSsoUser(true);
        setSsoProvider(userData.sso_provider);
      }

      const mfaResponse = await api.get('/api/mfa/status');
      setMfaEnabled(mfaResponse.data.mfaEnabled || false);
      await fetchMfaFactors();
      if (userData.role === 'SUPER_ADMIN' || userData.role === 'MASTER') {
        await fetchTwilioConfig();
      }
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const response = await api.get('/api/auth/activity');
      setRecentActivity(response.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  const handleMfaSetup = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/mfa/setup/initialize');
      setMfaSetup(response.data);
      setShowMfaSetup(true);
      toast.success('MFA setup initiated');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to setup MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerification = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      await api.post('/api/mfa/setup/complete', { token: verificationCode });
      setMfaEnabled(true);
      setShowMfaSetup(false);
      setShowBackupCodes(true);
      toast.success('MFA enabled successfully!');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const fetchMfaFactors = async () => {
    try {
      const response = await api.get('/api/mfa/factors');
      setMfaFactors(response.data.factors || []);
    } catch (error) {
      console.error('Failed to load MFA methods:', error);
    }
  };

  const handleSetDefaultFactor = async (factorId: number) => {
    try {
      await api.put(`/api/mfa/factors/${factorId}/default`);
      await fetchMfaFactors();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to set the default method');
    }
  };

  const handleRemoveFactor = async (factor: MfaFactor) => {
    if (!window.confirm(`Remove ${factor.label || factor.type} as a verification method?`)) return;
    try {
      await api.delete(`/api/mfa/factors/${factor.id}`);
      await fetchMfaFactors();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to remove the method');
    }
  };

  const handleSendEmailEnrolCode = async () => {
    try {
      setLoading(true);
      await api.post('/api/mfa/factors/email/initialize');
      setEmailEnrolSent(true);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to send the verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteEmailEnrol = async () => {
    try {
      setLoading(true);
      await api.post('/api/mfa/factors/email/complete', { code: emailEnrolCode });
      setEmailEnrolSent(false);
      setEmailEnrolCode('');
      setMfaEnabled(true);
      await fetchMfaFactors();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPasskey = async () => {
    try {
      setLoading(true);
      const init = await api.post('/api/mfa/factors/passkey/initialize');
      const attestation = await startRegistration({ optionsJSON: init.data.options });
      await api.post('/api/mfa/factors/passkey/complete', {
        response: attestation,
        label: 'Passkey',
      });
      setMfaEnabled(true);
      await fetchMfaFactors();
    } catch (error: any) {
      // A user who dismisses the browser prompt is not an error worth shouting about.
      if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') return;
      alert(error.response?.data?.error || error.message || 'Could not add that passkey');
    } finally {
      setLoading(false);
    }
  };

  const fetchTwilioConfig = async () => {
    try {
      const response = await api.get('/api/mfa/admin/twilio');
      setTwilioConfig(response.data.config);
      setTwilioForm((prev) => ({ ...prev, fromNumber: response.data.config.fromNumber || '' }));
    } catch (error) {
      // A non-super-admin simply does not get this panel.
    }
  };

  const handleTwilioTest = async () => {
    try {
      setTwilioTesting(true);
      setTwilioTestResult(null);
      const response = await api.post('/api/mfa/admin/twilio/test', twilioForm);
      setTwilioTestResult(
        `Connected to ${response.data.accountName || 'Twilio'} (${response.data.accountStatus})` +
        (response.data.fromNumberOwned === false ? ' — but that number is not on this account' : '')
      );
    } catch (error: any) {
      setTwilioTestResult(error.response?.data?.message || error.response?.data?.error || 'Connection failed');
    } finally {
      setTwilioTesting(false);
    }
  };

  const handleTwilioSave = async (enabled: boolean) => {
    try {
      setLoading(true);
      const response = await api.put('/api/mfa/admin/twilio', { ...twilioForm, enabled });
      setTwilioConfig(response.data.config);
      setTwilioForm((prev) => ({ ...prev, authToken: '' }));
      setTwilioTestResult(enabled ? 'Phone verification is on' : 'Saved');
    } catch (error: any) {
      setTwilioTestResult(error.response?.data?.detail || error.response?.data?.error || 'Could not save');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneEnrolCode = async () => {
    try {
      setLoading(true);
      await api.post('/api/mfa/factors/phone/initialize', {
        phoneNumber: phoneNumberInput,
        method: phoneEnrolMethod,
      });
      setPhoneEnrolStep('code');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Could not send the code');
    } finally {
      setLoading(false);
    }
  };

  const handleCompletePhoneEnrol = async () => {
    try {
      setLoading(true);
      await api.post('/api/mfa/factors/phone/complete', { code: phoneEnrolCode });
      setPhoneEnrolStep('idle');
      setPhoneEnrolCode('');
      setPhoneNumberInput('');
      setMfaEnabled(true);
      await fetchMfaFactors();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaDisable = async () => {
    setShowDisableConfirm(true);
  };

  const confirmMfaDisable = async () => {
    if (!disablePassword) {
      toast.error('Password is required');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/api/mfa/disable', { password: disablePassword });
      setMfaEnabled(false);
      setMfaSetup(null);
      setShowBackupCodes(false);
      setShowDisableConfirm(false);
      setDisablePassword('');
      toast.success('MFA disabled successfully');
    } catch (error: any) {
      console.error('MFA disable error:', error.response || error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success('Copied to clipboard');
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      if (profileData.newPassword) {
        if (profileData.newPassword !== profileData.confirmPassword) {
          toast.error('New passwords do not match');
          setLoading(false);
          return;
        }

        if (!profileData.currentPassword) {
          toast.error('Current password is required');
          setLoading(false);
          return;
        }

        if (passwordErrors.length > 0) {
          toast.error('Password does not meet requirements');
          setLoading(false);
          return;
        }

        if (mfaEnabled && !passwordMfaCode && !showPasswordMfaInput) {
          setShowPasswordMfaInput(true);
          toast('Please enter your MFA code to change password', { icon: '🔐' });
          setLoading(false);
          return;
        }

        if (mfaEnabled && !passwordMfaCode) {
          toast.error('MFA code is required to change password');
          setLoading(false);
          return;
        }
      }

      let hasUpdates = false;

      if (profileData.name !== user?.name) {
        await api.put('/api/auth/profile', {
          name: profileData.name
        });
        hasUpdates = true;
      }

      if (profileData.newPassword) {
        const response = await api.post('/api/auth/change-password', {
          currentPassword: profileData.currentPassword,
          newPassword: profileData.newPassword,
          mfaCode: mfaEnabled ? passwordMfaCode : undefined
        });
        hasUpdates = true;

        if (response.data.requireRelogin) {
          toast.success('Password changed successfully. Please log in again.');
          tokenStorage.clearTokens();
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
          return;
        }
      }

      if (hasUpdates) {
        if (profileData.newPassword && profileData.name !== user?.name) {
          toast.success('Profile and password updated successfully');
        } else if (profileData.newPassword) {
          toast.success('Password changed successfully');
        } else {
          toast.success('Profile updated successfully');
        }
      }

      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setPasswordMfaCode('');
      setShowPasswordMfaInput(false);
    } catch (error: any) {
      if (error.response?.data?.mfaRequired) {
        setShowPasswordMfaInput(true);
        toast('Please enter your MFA code to change password', { icon: '🔐' });
      } else if (error.response?.data?.ssoRedirect) {
        toast.error(`Please change your password through ${error.response.data.ssoProvider}`);
      } else if (error.response?.data?.passwordErrors) {
        toast.error(`Password validation failed: ${error.response.data.passwordErrors.join(', ')}`);
      } else {
        toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update');
      }
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!mfaSetup?.backup_codes) return;
    
    const content = `JARVIS RCSA - MFA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\nUser: ${user?.email}\n\n${mfaSetup.backup_codes.map((code, i) => `${i + 1}. ${code}`).join('\n')}\n\nStore these codes safely. Each code can only be used once.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'jarvis-mfa-backup-codes.txt';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'activity', label: 'Activity', icon: Activity },
    // Stemmler-only, but NOT admin-only: these are the person's own settings,
    // and most of the people who have them are not admins.
    ...(integrationsAllowed ? [{ id: 'notifications', label: 'Notifications', icon: Bell }] : []),
    // BD Assistant — Stemmler-only (same gate), a person's own settings plus the
    // admin rollout for admins.
    // TODO (BD module, Phase 1): BD-00/BD-01 place these under the BD module's own
    // "Settings" tab (alongside Radar/Meetings/Debriefs/...). That module shell
    // does not exist yet — only BDEngagements (BD Pipeline) does — so the BD
    // Assistant settings live here for now. Move this tab into the BD module's
    // Settings tab when that module is built, and drop it from here.
    ...(integrationsAllowed ? [{ id: 'bd-assistant', label: 'BD Assistant', icon: Briefcase }] : []),
    ...(user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER' ? [
      { id: 'system-security', label: 'System Security', icon: SettingsIcon }
    ] : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <SettingsIcon className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          </div>
          <p className="text-gray-600">Manage your account settings and security preferences</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center space-x-3 px-4 py-3 text-left rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Profile Information</h2>
                    <p className="text-gray-600">Update your personal information and account details</p>
                  </div>

                  <div className="space-y-6">
                    {/* Profile Picture Section */}
                    <div className="flex items-center space-x-6 pb-6 border-b border-gray-200">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
                        <p className="text-gray-500">{user?.email}</p>
                        <div className="flex items-center mt-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user?.role === 'SUPER_ADMIN' ? 'bg-red-100 text-red-800' :
                            user?.role === 'MASTER' ? 'bg-purple-100 text-purple-800' :
                            user?.role === 'ADMIN' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            <Shield className="w-3 h-3 mr-1" />
                            {user?.role}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Form */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={profileData.name}
                          onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter your full name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          value={profileData.email}
                          disabled
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                          placeholder="Email cannot be changed"
                        />
                      </div>
                    </div>

                    {/* Password Change Section */}
                    <div className="pt-6 border-t border-gray-200">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Current Password
                          </label>
                          <input
                            type="password"
                            value={profileData.currentPassword}
                            onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter current password"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                          </label>
                          <input
                            type="password"
                            value={profileData.newPassword}
                            onChange={(e) => handlePasswordChange(e.target.value)}
                            onFocus={() => setShowPasswordRequirements(true)}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              passwordErrors.length > 0 && profileData.newPassword ? 'border-red-300' : 'border-gray-300'
                            }`}
                            placeholder="Enter new password (12+ chars, 1 number, 1 special char)"
                          />

                          {/* Password Requirements */}
                          {(showPasswordRequirements || profileData.newPassword) && (
                            <div className="mt-2 p-3 bg-gray-50 rounded-lg border">
                              <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                              <div className="grid grid-cols-1 gap-1 text-xs">
                                <div className={`flex items-center ${profileData.newPassword.length >= 12 ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className="mr-1">{profileData.newPassword.length >= 12 ? '✓' : '✗'}</span>
                                  At least 12 characters long
                                </div>
                                <div className={`flex items-center ${/\d/.test(profileData.newPassword) ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className="mr-1">{/\d/.test(profileData.newPassword) ? '✓' : '✗'}</span>
                                  At least one number (0-9)
                                </div>
                                <div className={`flex items-center ${/[!@#$%^&*(),.?":{}|<>]/.test(profileData.newPassword) ? 'text-green-600' : 'text-red-500'}`}>
                                  <span className="mr-1">{/[!@#$%^&*(),.?":{}|<>]/.test(profileData.newPassword) ? '✓' : '✗'}</span>
                                  At least one special character (!@#$%^&*...)
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm Password
                          </label>
                          <input
                            type="password"
                            value={profileData.confirmPassword}
                            onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Confirm new password"
                          />
                        </div>

                        {showPasswordMfaInput && mfaEnabled && (
                          <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              <Smartphone className="inline w-4 h-4 mr-1" />
                              MFA Verification Code
                            </label>
                            <input
                              type="text"
                              value={passwordMfaCode}
                              onChange={(e) => setPasswordMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50"
                              placeholder="Enter 6-digit code from authenticator app"
                              maxLength={6}
                            />
                            <p className="text-sm text-blue-600 mt-1">
                              Enter the code from your authenticator app to verify this password change
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {ssoUser && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center">
                          <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
                          <p className="text-yellow-800">
                            You are signed in via {ssoProvider}. Password changes must be made through your identity provider.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end">
                      <button
                        onClick={handleProfileUpdate}
                        disabled={loading || (!!profileData.newPassword && passwordErrors.length > 0) || ssoUser}
                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                      >
                        {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        <span>Save Changes</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Security Settings</h2>
                    <p className="text-gray-600">Manage your account security and authentication methods</p>
                  </div>

                  <div className="space-y-6">
                    {/* MFA Section */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <Smartphone className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">Two-Factor Authentication</h3>
                            <p className="text-gray-600">Add an extra layer of security to your account</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            mfaEnabled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {mfaEnabled ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Enabled
                              </>
                            ) : (
                              <>
                                <AlertCircle className="w-3 h-3 mr-1" />
                                Disabled
                              </>
                            )}
                          </span>
                        </div>
                      </div>

                      {!mfaEnabled && !showMfaSetup && (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4 border border-blue-200">
                            <div className="flex items-center space-x-3 mb-2">
                              <Shield className="w-5 h-5 text-blue-600" />
                              <span className="font-medium text-gray-900">Enhanced Security</span>
                            </div>
                            <p className="text-gray-600 text-sm mb-4">
                              Two-factor authentication provides an additional security layer by requiring a code from your mobile device.
                            </p>
                            <ul className="text-sm text-gray-600 space-y-1 mb-4">
                              <li className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Works with Google Authenticator, Authy, and other TOTP apps</span>
                              </li>
                              <li className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Backup codes provided for account recovery</span>
                              </li>
                              <li className="flex items-center space-x-2">
                                <div className="w-1.5 h-1.5 bg-blue-600 rounded-full"></div>
                                <span>Required for accessing sensitive features</span>
                              </li>
                            </ul>
                          </div>
                          <button
                            onClick={handleMfaSetup}
                            disabled={loading}
                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                          >
                            {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                            <span>Enable Two-Factor Authentication</span>
                          </button>
                        </div>
                      )}

                      {showMfaSetup && mfaSetup && (
                        <div className="space-y-6">
                          <div className="bg-white rounded-lg p-6 border border-blue-200">
                            <h4 className="text-lg font-medium text-gray-900 mb-4">Setup Two-Factor Authentication</h4>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <h5 className="font-medium text-gray-900 mb-3">Step 1: Scan QR Code</h5>
                                <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center">
                                  <img 
                                    src={mfaSetup.qr_code}
                                    alt="MFA QR Code"
                                    className="w-48 h-48 mx-auto mb-3 border rounded-lg"
                                  />
                                  <p className="text-xs text-gray-600 text-center">
                                    Scan with Google Authenticator, Authy, or similar app
                                  </p>
                                </div>
                                
                                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Manual entry key:</span>
                                    <button
                                      onClick={() => copyToClipboard(mfaSetup.secret)}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      {copiedCode === mfaSetup.secret ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    </button>
                                  </div>
                                  <code className="text-xs font-mono text-gray-800 break-all">{mfaSetup.secret || 'Loading...'}</code>
                                </div>
                              </div>

                              <div>
                                <h5 className="font-medium text-gray-900 mb-3">Step 2: Verify Setup</h5>
                                <div className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                      Enter 6-digit code from your app
                                    </label>
                                    <input
                                      type="text"
                                      value={verificationCode}
                                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-2xl font-mono"
                                      placeholder="000000"
                                      maxLength={6}
                                    />
                                  </div>
                                  
                                  <div className="flex space-x-3">
                                    <button
                                      onClick={handleMfaVerification}
                                      disabled={loading || verificationCode.length !== 6}
                                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                    >
                                      {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                      <span>Verify & Enable</span>
                                    </button>
                                    <button
                                      onClick={() => setShowMfaSetup(false)}
                                      className="px-4 py-2 text-gray-600 hover:text-gray-800"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {mfaEnabled && (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg p-4 border border-green-200">
                            <div className="flex items-center space-x-3 mb-2">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                              <span className="font-medium text-gray-900">Two-Factor Authentication Active</span>
                            </div>
                            <p className="text-gray-600 text-sm">
                              Your account is protected with two-factor authentication. Choose which method you would rather use at sign-in.
                            </p>
                          </div>

                          <div className="bg-white rounded-lg border border-gray-200">
                            <div className="px-4 py-3 border-b border-gray-200">
                              <span className="font-medium text-gray-900">Your verification methods</span>
                            </div>
                            <ul className="divide-y divide-gray-100">
                              {mfaFactors.filter(f => f.verified).map((factor) => (
                                <li key={factor.id} className="px-4 py-3 flex items-center justify-between">
                                  <div className="flex items-center space-x-3">
                                    <Shield className="w-4 h-4 text-gray-400" />
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">
                                        {MFA_METHOD_LABELS[factor.type] || factor.type}
                                        {factor.isDefault && (
                                          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Default</span>
                                        )}
                                      </div>
                                      {factor.label && factor.label !== MFA_METHOD_LABELS[factor.type] && (
                                        <div className="text-xs text-gray-500">{factor.label}</div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3">
                                    {!factor.isDefault && (
                                      <button
                                        onClick={() => handleSetDefaultFactor(factor.id)}
                                        className="text-sm text-blue-600 hover:text-blue-800 underline"
                                      >
                                        Make default
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleRemoveFactor(factor)}
                                      className="text-sm text-red-600 hover:text-red-800 underline"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>

                            {!mfaFactors.some(f => f.type === 'passkey' && f.verified) && (
                              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                                <button
                                  onClick={handleAddPasskey}
                                  disabled={loading}
                                  className="text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                                >
                                  Add a passkey (Face ID, Touch ID, or security key)
                                </button>
                                <p className="text-xs text-gray-500 mt-1">
                                  Passkeys cannot be phished and need no code to type.
                                </p>
                              </div>
                            )}

                            {twilioConfig?.enabled && !mfaFactors.some(f => f.type === 'sms' || f.type === 'voice') && (
                              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                                {phoneEnrolStep === 'idle' ? (
                                  <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="tel"
                                        value={phoneNumberInput}
                                        onChange={(e) => setPhoneNumberInput(e.target.value)}
                                        placeholder="+14155550123"
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-44"
                                      />
                                      <select
                                        value={phoneEnrolMethod}
                                        onChange={(e) => setPhoneEnrolMethod(e.target.value as 'sms' | 'voice')}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                      >
                                        <option value="sms">Text message</option>
                                        <option value="voice">Phone call</option>
                                      </select>
                                      <button
                                        onClick={handleSendPhoneEnrolCode}
                                        disabled={loading || !phoneNumberInput}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
                                      >
                                        Send code
                                      </button>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                      Use international format, starting with your country code.
                                    </p>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      value={phoneEnrolCode}
                                      onChange={(e) => setPhoneEnrolCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                      placeholder="000000"
                                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm tracking-widest w-32"
                                    />
                                    <button
                                      onClick={handleCompletePhoneEnrol}
                                      disabled={loading || phoneEnrolCode.length !== 6}
                                      className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
                                    >
                                      Verify
                                    </button>
                                    <button
                                      onClick={() => { setPhoneEnrolStep('idle'); setPhoneEnrolCode(''); }}
                                      className="text-sm text-gray-600 hover:text-gray-800 underline"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}

                            {!mfaFactors.some(f => f.type === 'email' && f.verified) && (
                              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                                {!emailEnrolSent ? (
                                  <button
                                    onClick={handleSendEmailEnrolCode}
                                    disabled={loading}
                                    className="text-sm text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                                  >
                                    Add email as a verification method
                                  </button>
                                ) : (
                                  <div className="space-y-2">
                                    <p className="text-sm text-gray-600">
                                      We sent a 6-digit code to your account email. Enter it to finish adding this method.
                                    </p>
                                    <div className="flex items-center space-x-2">
                                      <input
                                        type="text"
                                        inputMode="numeric"
                                        value={emailEnrolCode}
                                        onChange={(e) => setEmailEnrolCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        placeholder="000000"
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm tracking-widest w-32"
                                      />
                                      <button
                                        onClick={handleCompleteEmailEnrol}
                                        disabled={loading || emailEnrolCode.length !== 6}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
                                      >
                                        Verify
                                      </button>
                                      <button
                                        onClick={() => { setEmailEnrolSent(false); setEmailEnrolCode(''); }}
                                        className="text-sm text-gray-600 hover:text-gray-800 underline"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex space-x-3">
                            <button
                              onClick={() => setShowBackupCodes(!showBackupCodes)}
                              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                            >
                              <Eye className="w-4 h-4" />
                              <span>{showBackupCodes ? 'Hide' : 'View'} Backup Codes</span>
                            </button>
                            <button
                              onClick={handleMfaDisable}
                              disabled={loading}
                              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                              <span>Disable MFA</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {showBackupCodes && mfaEnabled && (
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="w-5 h-5 text-yellow-600" />
                              <span className="font-medium text-gray-900">Backup Codes</span>
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Backup codes are only displayed during MFA setup for security reasons. If you need new backup codes, disable and re-enable MFA.
                          </p>
                          <div className="text-sm text-blue-600">
                            <button
                              onClick={() => setShowBackupCodes(false)}
                              className="underline hover:text-blue-800"
                            >
                              Hide this message
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {showBackupCodes && mfaSetup?.backup_codes && (
                        <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2">
                              <AlertCircle className="w-5 h-5 text-yellow-600" />
                              <span className="font-medium text-gray-900">Backup Codes</span>
                            </div>
                            <button
                              onClick={downloadBackupCodes}
                              className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                            >
                              <Download className="w-4 h-4" />
                              <span className="text-sm">Download</span>
                            </button>
                          </div>
                          <p className="text-sm text-gray-600 mb-4">
                            Store these codes safely. Each can only be used once if you lose access to your authenticator app.
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            {mfaSetup.backup_codes.map((code, index) => (
                              <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                                <code className="text-sm font-mono">{code}</code>
                                <button
                                  onClick={() => copyToClipboard(code)}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {copiedCode === code ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Session Security */}
                    <div className="bg-gray-50 rounded-xl p-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <Lock className="w-6 h-6 text-gray-600" />
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Session Security</h3>
                          <p className="text-gray-600">Manage your active sessions and security settings</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center space-x-2 mb-2">
                            <Globe className="w-4 h-4 text-blue-600" />
                            <span className="font-medium text-gray-900">Current Session</span>
                          </div>
                          <p className="text-sm text-gray-600">Active since login</p>
                          <p className="text-xs text-gray-500 mt-1">Auto-logout after 4 hours of inactivity</p>
                        </div>
                        
                        <div className="bg-white p-4 rounded-lg border">
                          <div className="flex items-center space-x-2 mb-2">
                            <Calendar className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-gray-900">Password Last Changed</span>
                          </div>
                          <p className="text-sm text-gray-600">Recently updated</p>
                          <p className="text-xs text-gray-500 mt-1">Strong password detected</p>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div className="p-6">
                  <div className="mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Activity</h2>
                    <p className="text-gray-600">Monitor your recent account activity and login history</p>
                  </div>

                  <div className="space-y-4">
                    {recentActivity.length > 0 ? (
                      recentActivity.map((activity) => (
                        <div key={activity.id} className="bg-gray-50 rounded-lg p-4 border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="p-2 bg-blue-100 rounded-full">
                                <Activity className="w-4 h-4 text-blue-600" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{activity.action}</p>
                                <p className="text-sm text-gray-600">
                                  {activity.ip_address} • {new Date(activity.created_at).toLocaleString()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No Recent Activity</h3>
                        <p className="text-gray-600">Your account activity will appear here</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notifications Tab */}
              {activeTab === 'notifications' && (
                <ExternalIntegrationsGate fallback={null}>
                  <div className="p-6">
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold text-gray-900 mb-2">Notifications</h2>
                      <p className="text-gray-600">
                        Choose what Jarvis tells you in Slack, and when.
                      </p>
                    </div>
                    <SlackNotificationSettings />
                  </div>
                </ExternalIntegrationsGate>
              )}

              {activeTab === 'bd-assistant' && (
                <ExternalIntegrationsGate fallback={null}>
                  <div className="p-6 space-y-10">
                    <BDAssistantSettings />
                    {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER') && (
                      <div className="pt-8 border-t border-gray-100">
                        <BDAssistantRolloutCard />
                      </div>
                    )}
                    {/* BD-10: the firm's house rules (admin routes: ADMIN / SUPER_ADMIN). */}
                    {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                      <div className="pt-8 border-t border-gray-100">
                        <BDFollowUpFirmRulesCard />
                      </div>
                    )}
                  </div>
                </ExternalIntegrationsGate>
              )}

              {activeTab === 'system-security' && (user?.role === 'SUPER_ADMIN' || user?.role === 'MASTER') && (
                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <SettingsIcon className="w-6 h-6 text-red-600" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">System Security Settings</h2>
                      <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                        SUPER ADMIN ONLY
                      </span>
                    </div>
                    <p className="text-gray-600">Configure system-wide security policies and compliance settings</p>
                  </div>

                  <div className="mb-6 bg-white rounded-lg border border-gray-200">
                    <div className="px-4 py-3 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-medium text-gray-900">Phone verification (Twilio)</span>
                          <p className="text-xs text-gray-500 mt-1">
                            Paste your Twilio credentials to offer text-message and phone-call verification.
                            Until this is on, those options stay hidden from everyone.
                          </p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          twilioConfig?.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {twilioConfig?.enabled ? 'Enabled' : twilioConfig?.configured ? 'Configured, off' : 'Not configured'}
                        </span>
                      </div>
                    </div>

                    <div className="p-4 space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Account SID</label>
                        <input
                          type="text"
                          value={twilioForm.accountSid}
                          onChange={(e) => setTwilioForm({ ...twilioForm, accountSid: e.target.value })}
                          placeholder={twilioConfig?.accountSid || 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Auth Token</label>
                        <input
                          type="password"
                          value={twilioForm.authToken}
                          onChange={(e) => setTwilioForm({ ...twilioForm, authToken: e.target.value })}
                          placeholder={twilioConfig?.hasAuthToken ? 'Stored — leave blank to keep it' : 'Your Twilio auth token'}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Encrypted before it is written, and never sent back to this screen.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From number</label>
                        <input
                          type="text"
                          value={twilioForm.fromNumber}
                          onChange={(e) => setTwilioForm({ ...twilioForm, fromNumber: e.target.value })}
                          placeholder="+14155550000"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                        />
                      </div>

                      {twilioTestResult && (
                        <div className="text-sm px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-700">
                          {twilioTestResult}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-1">
                        <button
                          onClick={handleTwilioTest}
                          disabled={twilioTesting}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50"
                        >
                          {twilioTesting ? 'Testing…' : 'Test connection'}
                        </button>
                        <button
                          onClick={() => handleTwilioSave(true)}
                          disabled={loading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          Save &amp; enable
                        </button>
                        {twilioConfig?.enabled && (
                          <button
                            onClick={() => handleTwilioSave(false)}
                            disabled={loading}
                            className="px-4 py-2 border border-red-300 text-red-700 rounded-lg text-sm hover:bg-red-50 disabled:opacity-50"
                          >
                            Turn off
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        Save &amp; enable runs the connection test first — bad credentials will not switch it on.
                      </p>
                    </div>
                  </div>

                  <SystemSecuritySettings />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MFA Disable Confirmation Modal */}
      {showDisableConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Shield className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Disable Two-Factor Authentication</h3>
                <p className="text-sm text-gray-600">This will make your account less secure</p>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Enter your password to confirm
              </label>
              <input
                type="password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Enter your password"
                autoFocus
              />
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <span className="text-sm font-medium text-yellow-800">Warning</span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                Disabling MFA will make your account vulnerable to unauthorized access. You can re-enable it anytime.
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDisableConfirm(false);
                  setDisablePassword('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmMfaDisable}
                disabled={loading || !disablePassword}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Disabling...</span>
                  </>
                ) : (
                  <>
                    <X className="w-4 h-4" />
                    <span>Disable MFA</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;